import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { companiesApi, organizationApi } from '@/features/organization/api'
import type { CompanyCreatePayload, CompanyUpdatePayload, OrganizationUpdatePayload } from '@/features/organization/types'

const COMPANIES_KEY = ['companies'] as const
const ORGANIZATION_KEY = ['organization', 'me'] as const

export function useCompanies(params: { offset: number; limit: number; q?: string }) {
  return useQuery({
    queryKey: [...COMPANIES_KEY, params],
    queryFn: () => companiesApi.list(params),
    placeholderData: (previous) => previous,
  })
}

export function useCreateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CompanyCreatePayload) => companiesApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COMPANIES_KEY }),
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, payload }: { companyId: string; payload: CompanyUpdatePayload }) =>
      companiesApi.update(companyId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COMPANIES_KEY }),
  })
}

export function useDeleteCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (companyId: string) => companiesApi.remove(companyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COMPANIES_KEY }),
  })
}

/**
 * UX-2.1 — lectura de la organización activa para el contexto del Product
 * Shell (`TopContextBar`, sección "Active organization/territory context"
 * del encargo). Mismo `queryKey` que `OrganizationPage` (`['organization',
 * 'me']`) — comparte caché, no duplica la llamada a `GET /organizations/me`.
 * `retry: false` y sin lanzar en error: si la cuenta aún no tiene
 * organización (403) o hay un fallo real, el shell simplemente no muestra
 * el nombre — nunca un nombre inventado.
 */
export function useOrganization() {
  return useQuery({
    queryKey: ORGANIZATION_KEY,
    queryFn: organizationApi.me,
    retry: false,
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OrganizationUpdatePayload) => organizationApi.update(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEY }),
  })
}

export function useDeleteOrganization() {
  return useMutation({
    mutationFn: () => organizationApi.remove(),
  })
}
